import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ClientAutocomplete.css";

const API_BASE = "https://kanban.amservices.eu/api";

/**
 * Champ Client avec autocomplétion Henrri (suggestions à partir de 3 caractères).
 *
 * Props :
 *   value      : string                      — valeur du champ (nom client)
 *   onChange   : (nom: string) => void        — appelé à chaque frappe
 *   onSelect   : (client) => void (optionnel) — appelé au choix d'une suggestion.
 *                                               client = { id, nom, type, siret, ville }
 *   required   : bool (optionnel)
 *   placeholder: string (optionnel)
 */
const ClientAutocomplete = ({ value, onChange, onSelect, required = false, placeholder = "" }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [noResult, setNoResult] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    const wrapRef = useRef(null);
    const debounceRef = useRef(null);
    const justSelectedRef = useRef(false);

    const fetchSuggestions = useCallback(async (term) => {
        setLoading(true);
        setNoResult(false);
        try {
            const res = await fetch(`${API_BASE}/get_henrri_clients.php?search=${encodeURIComponent(term)}`);
            const data = await res.json();
            if (data.success) {
                setSuggestions(data.data);
                setNoResult(data.data.length === 0);
                setOpen(true);
            } else {
                setSuggestions([]);
                setNoResult(true);
                setOpen(true);
            }
        } catch (err) {
            console.error(err);
            setSuggestions([]);
            setNoResult(true);
            setOpen(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // Déclenche la recherche (debounced) quand la valeur change et >= 3 car.
    useEffect(() => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const term = (value || "").trim();
        if (term.length < 3) {
            setSuggestions([]);
            setOpen(false);
            setNoResult(false);
            return;
        }
        debounceRef.current = setTimeout(() => fetchSuggestions(term), 300);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [value, fetchSuggestions]);

    // Fermeture au clic à l'extérieur
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (client) => {
        justSelectedRef.current = true;
        onChange(client.nom || "");
        if (typeof onSelect === "function") onSelect(client);
        setOpen(false);
        setSuggestions([]);
        setHighlight(-1);
    };

    const handleKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter" && highlight >= 0) {
            e.preventDefault();
            handleSelect(suggestions[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="client-autocomplete" ref={wrapRef}>
            <input
                type="text"
                value={value}
                required={required}
                placeholder={placeholder}
                autoComplete="off"
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
            />

            {open && (
                <div className="client-suggestions">
                    {loading ? (
                        <div className="client-sugg-state">Recherche…</div>
                    ) : noResult ? (
                        <div className="client-sugg-state">
                            <span>Aucun client trouvé</span>
                            <button
                                type="button"
                                className="client-sugg-close"
                                onClick={() => setOpen(false)}
                            >Fermer ✕</button>
                        </div>
                    ) : (
                        suggestions.map((c, idx) => (
                            <div
                                key={c.id ?? idx}
                                className={`client-sugg-item ${idx === highlight ? "active" : ""}`}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                            >
                                <span className="client-sugg-nom">{c.nom}</span>
                                <span className="client-sugg-meta">
                                    {c.ville ? c.ville : ""}
                                    {c.type === "professional" ? " · Pro" : c.type === "individual" ? " · Particulier" : ""}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientAutocomplete;
